// formatTime v0.3.0 — header time helper. Three clocks:
//   1. Visitor's local wall clock with their TZ abbreviation (e.g. "EDT") AND
//      a numeric UTC offset (e.g. "(UTC-4)") that follows DST automatically
//      because Date.getTimezoneOffset() reflects the active offset.
//   2. GMT (UTC) — invariant reference.
//   3. Perth, Western Australia (UTC+8, no DST) — covers both Perth and
//      mainland China since they share the same offset year-round.
// Pure: takes a Date, returns a string. No DOM, no Intl singletons cached at
// module scope — the cost of constructing the formatter per second is
// negligible compared to a re-render and avoids stale-locale bugs in tests.

// Format the visitor's local UTC offset as "+H" / "-H" / "+H:MM" — handles
// the half-hour and quarter-hour zones (India +5:30, Newfoundland -3:30,
// Nepal +5:45) correctly. Date.getTimezoneOffset() returns minutes WEST of
// UTC; we negate to get the conventional sign.
function formatUtcOffset(d: Date): string {
  const minutesEastOfUtc = -d.getTimezoneOffset();
  const sign = minutesEastOfUtc >= 0 ? '+' : '-';
  const absMin = Math.abs(minutesEastOfUtc);
  const hours = Math.trunc(absMin / 60);
  const minutes = absMin % 60;
  return minutes === 0 ? `${sign}${hours}` : `${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

export function formatHeaderTimes(d: Date): string {
  const local = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  });
  const offset = formatUtcOffset(d);

  const gmt = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });

  // Perth (UTC+8, no DST). Same offset as mainland China year-round, so the
  // single column doubles as a reference for both.
  const perth = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Australia/Perth',
  });

  return `${local} (UTC${offset}) · ${gmt} GMT · ${perth} Perth (UTC+8)`;
}

// 24h cutoff for separating "active" (recent) issues from "long-term" entries
// that have not been updated for a full day or more. The shared constant +
// helper keep the IssuesCard and LongTermIssuesCard partitions in sync.
export const LONG_TERM_THRESHOLD_MS = 24 * 60 * 60 * 1000;

export function isLongTerm(updatedAtOrSince: string | undefined, now: Date = new Date()): boolean {
  if (!updatedAtOrSince) return false;
  const ts = Date.parse(updatedAtOrSince);
  if (Number.isNaN(ts)) return false;
  return now.getTime() - ts >= LONG_TERM_THRESHOLD_MS;
}

// Compact relative-time for "detected at" labels in the IssuesCard bottom row.
// Optimised for short width — we already pay for the tier label and message
// in the same line, so this stays terse: '<1m', '12m', '3h47m', '2d', '5w'.
// 'just now' is preferred over '0m' for readability when the gap is sub-minute.
export function timeAgo(iso: string, now: Date = new Date()): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const sec = Math.max(0, Math.floor((now.getTime() - ts) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  if (hr < 24) return remMin === 0 ? `${hr}h ago` : `${hr}h${remMin}m ago`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  return `${wk}w ago`;
}
